import isEmpty from "lodash/isEmpty";
import { useReassignData } from "PFApp/booking/components/details_panel/detail_view/reassign_side_panel/hooks/use_reassign_data";
import { ProfileSource } from "PFApp/components/profile_source_toggle";
import { useTemplateFind } from "PFApp/hooks";
import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { useIsExternalUsage } from "PFCore/hooks/queries/bookings/use_is_external_usage";
import { Activity, Booking, PermissionRule } from "PFTypes";
import { useTranslation } from "react-i18next";

import { DisallowReasons, getAllowedTargetsWithData } from "./get_allowed_targets_with_data";

type IsReassignAllowed = {
  bookingIds: number[];
};
type IsReassignAllowedReturn = {
  isReassignVisible: boolean;
  isReassignAllowed: boolean;
  isLoading: boolean;
  allowedTargets: ProfileSource[];
  allowedActivities: Activity[];
  notAllowedActivities: Activity[];
  allowedBookings: Omit<Booking, "activity">[];
  notAllowedBookings: Omit<Booking, "activity">[];
  disabledMessage: string | null;
};

export const useIsReassignAllowed = ({ bookingIds }: IsReassignAllowed): IsReassignAllowedReturn => {
  const { t } = useTranslation("bookingModule");
  const findTemplate = useTemplateFind();
  const isExternal = useIsExternalUsage();
  const canReassignBookingsMatches = useIsCurrentUserPermittedTo(PermissionRule.ReassignBookingsMatches);
  const canReassignBookingsAllResources = useIsCurrentUserPermittedTo(
    PermissionRule.ReassignBookingsAllResources
  );
  const canReassign = canReassignBookingsMatches || canReassignBookingsAllResources;

  const { bookings, activities, isLoading } = useReassignData({ bookingIds });

  const getDisabledMessage = (disallowReasons: DisallowReasons) => {
    if (disallowReasons.differentAuditRoles) {
      return t("bookings.reassign.auditBookingsDisabledMessage");
    }

    if (disallowReasons.notSingleAuditRoleRelated) {
      return t("bookings.reassign.notSingleAuditRoleRelated");
    }

    return null;
  };

  if (!canReassign || isEmpty(bookingIds) || isExternal || isLoading) {
    return {
      isReassignVisible: canReassign,
      isReassignAllowed: false,
      isLoading,
      allowedTargets: [],
      allowedBookings: [],
      notAllowedBookings: bookings,
      allowedActivities: [],
      notAllowedActivities: activities,
      disabledMessage: null
    };
  }

  const { allowedTargets, allowedBookings, allowedActivities, disallowReasons } = getAllowedTargetsWithData({
    bookings,
    activities,
    findTemplate,
    canReassignBookingsMatches,
    canReassignBookingsAllResources
  });

  const disabledMessage = getDisabledMessage(disallowReasons);

  if (isEmpty(allowedTargets)) {
    return {
      isReassignVisible: true,
      isReassignAllowed: false,
      isLoading: false,
      allowedTargets: [],
      allowedBookings: [],
      notAllowedBookings: bookings,
      allowedActivities: [],
      notAllowedActivities: activities,
      disabledMessage
    };
  }

  const allowedBookingIds = allowedBookings.map(({ id }) => id);
  const allowedActivityIds = allowedActivities.map(({ id }) => id);
  const notAllowedBookings = bookings.filter(({ id }) => !allowedBookingIds.includes(id));
  const notAllowedActivities = activities.filter(({ id }) => !allowedActivityIds.includes(id));

  return {
    isReassignVisible: true,
    isReassignAllowed: true,
    isLoading: false,
    allowedTargets,
    allowedBookings,
    notAllowedBookings,
    allowedActivities,
    notAllowedActivities,
    disabledMessage
  };
};
